import React from 'react';
import { Building2, Gauge, Package, FlaskConical, Warehouse } from 'lucide-react';
import { infrastructure } from '../mockData';

const facilityIcons = {
  1: Building2,
  2: Gauge,
  3: Package,
  4: FlaskConical,
  5: Warehouse
};

const Infrastructure = () => {
  return (
    <section id="infrastructure" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            {infrastructure.title}
          </h2>
          <div className="w-24 h-1 bg-blue-600 mx-auto mb-6"></div>
          <p className="text-lg text-slate-600 leading-relaxed">
            {infrastructure.description}
          </p>
        </div>

        {/* Facilities Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {infrastructure.facilities.map((facility) => {
            const Icon = facilityIcons[facility.id];
            return (
              <div 
                key={facility.id}
                className="bg-white rounded-xl overflow-hidden border-2 border-slate-100 hover:border-blue-600 transition-all duration-300 hover:shadow-xl group"
              >
                {/* Machine Image */}
                {facility.image && (
                  <div className="relative h-72 overflow-hidden bg-slate-50 flex items-center justify-center p-4">
                    <img 
                      src={facility.image}
                      alt={facility.name}
                      className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                )}
                
                {/* Content */}
                <div className="p-8">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Icon className="text-white" size={24} />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                        {facility.name}
                      </h3>
                    </div>
                  </div>
                  <p className="text-slate-600 mb-4 leading-relaxed">
                    {facility.details}
                  </p>
                  <div className="flex items-center gap-2 pt-4 border-t border-slate-200">
                    <div className="flex-1">
                      <div className="text-sm text-slate-500 mb-1">Specification</div>
                      <div className="font-bold text-blue-600">{facility.capacity}</div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Facility Highlights */}
        <div className="grid lg:grid-cols-2 gap-8 items-center mt-16">
          <div className="relative rounded-2xl overflow-hidden shadow-xl">
            <img 
              src="https://customer-assets.emergentagent.com/job_sharad-enterprise/artifacts/r10d0a3w_image.png"
              alt="Pharmaceutical Leaflets Production"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="space-y-6">
            <h3 className="text-3xl font-bold text-slate-900">
              Built for Precision, Designed for Excellence
            </h3>
            <p className="text-slate-600 leading-relaxed">
              Our facility combines international-grade machinery with skilled craftsmanship. 
              Every piece of equipment is carefully selected to ensure pharmaceutical leaflets 
              meet the highest standards of accuracy, consistency, and regulatory compliance.
            </p>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <div className="font-semibold text-slate-900">24/7 Production Capability</div>
                  <div className="text-slate-600 text-sm">Round-the-clock operations to meet urgent deadlines and high-volume orders</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <div className="font-semibold text-slate-900">Sustainable Practices</div>
                  <div className="text-slate-600 text-sm">Eco-friendly processes with waste reduction and responsible resource management</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Infrastructure;
